import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Plus, Edit, Trash2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '', unit_cost: '', weight_kg: '', selling_price: ''
  });

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products`, { withCredentials: true });
      setProducts(res.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      unit_cost: parseFloat(formData.unit_cost) || 0,
      weight_kg: parseFloat(formData.weight_kg) || 0,
      selling_price: parseFloat(formData.selling_price) || 0
    };
    try {
      if (editingProduct) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/products/${editingProduct.id}`, payload, { withCredentials: true });
        toast.success('Produit modifié');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/products`, payload, { withCredentials: true });
        toast.success('Produit ajouté');
      }
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (p) => {
    setEditingProduct(p);
    setFormData({ name: p.name, unit_cost: String(p.unit_cost), weight_kg: String(p.weight_kg), selling_price: String(p.selling_price || 0) });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/products/${id}`, { withCredentials: true });
      toast.success('Produit supprimé');
      fetchProducts();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({ name: '', unit_cost: '', weight_kg: '', selling_price: '' });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Produits</h1>
            <p className="text-sm text-slate-600 mt-1">Catalogue des produits avec coût de revient et poids</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-product-button" className="bg-slate-900 hover:bg-slate-800">
                <Plus size={18} className="mr-2" />
                Nouveau produit
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom du produit*</Label>
                  <Input data-testid="product-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="T-shirt, Hoodie..." />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Coût de revient*</Label>
                    <Input data-testid="product-cost-input" type="number" step="0.01" min="0" value={formData.unit_cost} onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })} required placeholder="0.00" />
                  </div>
                  <div className="space-y-2">
                    <Label>Poids (kg)*</Label>
                    <Input data-testid="product-weight-input" type="number" step="0.01" min="0" value={formData.weight_kg} onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })} required placeholder="0.00" />
                  </div>
                  <div className="space-y-2">
                    <Label>Prix de vente</Label>
                    <Input data-testid="product-price-input" type="number" step="0.01" min="0" value={formData.selling_price} onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })} placeholder="0.00" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Annuler</Button>
                  <Button data-testid="submit-product-button" type="submit" className="bg-slate-900 hover:bg-slate-800">{editingProduct ? 'Modifier' : 'Créer'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Package size={20} className="text-slate-600" />
              <CardTitle className="text-lg font-medium">{products.length} produit(s)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : products.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucun produit. Ajoutez votre premier produit.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">Nom du produit</TableHead>
                    <TableHead className="font-medium text-right">Coût de revient</TableHead>
                    <TableHead className="font-medium text-right">Poids (kg)</TableHead>
                    <TableHead className="font-medium text-right">Prix de vente</TableHead>
                    <TableHead className="font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} data-testid="product-row" className="hover:bg-slate-50">
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-right">{p.unit_cost.toFixed(2)} &euro;</TableCell>
                      <TableCell className="text-right">{p.weight_kg.toFixed(2)} kg</TableCell>
                      <TableCell className="text-right">{(p.selling_price || 0).toFixed(2)} &euro;</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button data-testid="edit-product-button" size="sm" variant="ghost" onClick={() => handleEdit(p)} className="h-8 w-8 p-0"><Edit size={16} /></Button>
                          <Button data-testid="delete-product-button" size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 size={16} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
